import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideChevronDown, lucideChevronRight } from "@ng-icons/lucide";
import { HlmIconImports } from "@o2-fe/ui";
import { O2AccordionGroupComponent } from "./o2-accordion-group.component";

/**
 * O2 Accordion Item Component
 *
 * A styled accordion item that matches O2 design system.
 * Works with O2AccordionGroupComponent to ensure only one item is open at a time.
 *
 * Features:
 * - Automatic chevron icon (down when open, right when closed)
 * - White background with rounded corners
 * - Consistent padding and spacing
 * - Font: semibold, text-sm, text-foreground
 * - Smooth height animation
 *
 * Usage:
 * <o2-accordion-group>
 *   <o2-accordion-item label="Info">
 *     <div class="p-5 pt-0 flex flex-col gap-3.5">
 *       <!-- Your custom content here -->
 *     </div>
 *   </o2-accordion-item>
 * </o2-accordion-group>
 *
 * Note: The content should include its own padding (typically p-5 pt-0)
 */
@Component({
  selector: "o2-accordion-item",
  standalone: true,
  imports: [NgIcon, HlmIconImports],
  providers: [
    provideIcons({
      lucideChevronDown,
      lucideChevronRight,
    }),
  ],
  template: `
    <div class="rounded-lg bg-white dark:bg-black">
      <button
        class="w-full rounded-lg flex items-center justify-start p-5 cursor-pointer border-none bg-transparent hover:bg-muted/50 transition-colors"
        type="button"
        (click)="toggle()"
      >
        <span class="font-semibold text-sm text-foreground">{{ label() }}</span>
        <ng-icon hlm size="sm" class="ml-2.5" [name]="iconName()" />
      </button>
      @if (isOpen()) {
        <div class="overflow-hidden">
          <ng-content />
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class O2AccordionItemComponent implements OnInit, OnDestroy {
  // Inject parent accordion group
  private readonly _group = inject(O2AccordionGroupComponent);

  // Label input - displayed in the accordion trigger
  public readonly label = input.required<string>();

  // Computed: is this item open?
  // Public so parent components can access it via template reference
  public readonly isOpen = computed(() => this._group.isItemOpen(this.label()));

  // Computed icon name based on open/closed state
  protected readonly iconName = computed(() =>
    this.isOpen() ? "lucideChevronDown" : "lucideChevronRight",
  );

  ngOnInit(): void {
    // Register this item with the parent group using its label
    this._group.registerItem(this.label());
  }

  ngOnDestroy(): void {
    // Unregister when destroyed
    this._group.unregisterItem(this.label());
  }

  // Toggle open/closed state via parent group
  protected toggle(): void {
    this._group.toggleItem(this.label());
  }
}
